import React from 'react';
import PropTypes from 'prop-types';
import ChatContainer from 'modules/Chat/ChatContainer';
import { useTranslation } from 'react-i18next';
import { Button } from '@ontruck/orc';
import NoMessages from 'assets/images/noMessages.png';
import { useConversation } from 'hooks/useConversation/useConversation';
import DateFormat from 'components/DateFormat';
import moment from 'moment-timezone';
import { CHAT_ROUTES } from 'utils/constants/chat';

const ChatList = ({ className }) => {
  const { t } = useTranslation('Chat');
  const {
    conversations,
    handleGoToRoute,
    handleFetchAndGoToConversation,
    conversationsError
  } = useConversation();

  const renderConversations = () => {
    if (conversationsError) {
      return (
        <div
          className="chat-list__conversation-list"
          data-testid="chat-list__error"
        >
          <span className="chat-list__error">{t('generalError')}</span>
        </div>
      );
    }

    if (!conversations) {
      return (
        <div
          className="chat-list__conversation-list"
          data-testid="chat-list__loading"
        >
          {[...Array(7).keys()].map(key => (
            <div
              className="chat-list__conversation chat-list__conversation--loading"
              key={key}
            />
          ))}
        </div>
      );
    }

    if (conversations.length === 0) {
      return (
        <div className="chat-list__empty" data-testid="chat-list__empty">
          <img
            className="chat-list__empty-image"
            src={NoMessages}
            alt={t('noConversationsTitle')}
          />
          <div className="chat-list__empty-title">
            {t('noConversationsTitle')}
          </div>
          <div className="chat-list__empty-subtitle">
            {t('noConversationsSubtitle')}
          </div>
        </div>
      );
    }

    return (
      <div className="chat-list__conversation-list">
        {conversations.map(conversation => {
          const isLastMessageFromAgent = !!conversation.last_message?.sender
            ?.agent;
          return (
            <div
              className="chat-list__conversation"
              data-testid="chat-list__conversation"
              key={conversation.id}
              onClick={() =>
                handleFetchAndGoToConversation(conversation.id, 'Chat list')
              }
            >
              {conversation.journey && (
                <div
                  className="chat-list__conversation-header"
                  data-testid="chat-list__journey-header"
                >
                  <span className="chat-list__conversation-date">
                    <DateFormat date={conversation.journey?.start_time_from} />
                  </span>
                  <span className="chat-list__conversation-separator">{` | `}</span>
                  <span
                    className="chat-list__conversation-id"
                    data-testid="chat-list__conversation-subject"
                  >
                    {conversation.subject}
                  </span>
                </div>
              )}
              <div className="chat-list__conversation-body">
                <span
                  className="chat-list__conversation-title"
                  data-testid="chat-list__conversation-title"
                >
                  {conversation.journey
                    ? `${conversation?.journey?.country_from || ''} ${
                        conversation.journey?.label_from
                      } > ${conversation?.journey?.country_to || ''} ${
                        conversation.journey?.label_to
                      }`
                    : conversation.subject}
                </span>
                {conversation.has_unread_messages && (
                  <span
                    className={`chat-list__conversation-badge`}
                    data-testid="chat-list__badge"
                  />
                )}
              </div>
              {conversation.last_message && (
                <div className="chat-list__conversation-footer">
                  <div className="chat-list__conversation-message-container">
                    <span
                      className={`chat-list__conversation-author ${isLastMessageFromAgent &&
                        'chat-list__conversation-author--agent'}`}
                      data-testid="chat-list__conversation-author"
                    >
                      {`${
                        isLastMessageFromAgent
                          ? conversation.last_message?.sender?.name
                          : t('you')
                      }: `}
                    </span>
                    <span
                      className={`chat-list__conversation-message ${
                        conversation.has_unread_messages
                          ? 'chat-list__conversation-message--unread'
                          : ''
                      }`}
                    >
                      {conversation.last_message?.message}
                    </span>
                  </div>
                  <span className="chat-list__conversation-time">
                    {moment(conversation.last_message?.created_at).fromNow()}
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className={className} data-testid="chat-list">
      <div className="chat-list">
        <ChatContainer title={t('support')} withPadding>
          <div className="chat-list__container">
            <h4 className="chat-list__title">{t('conversations')}</h4>
            {renderConversations()}
            <div className="chat-list__footer">
              <Button
                className="chat-list__button"
                text={t('newConversation')}
                skin="primary"
                data-testid="chat-list__new-conversation"
                onClick={() => handleGoToRoute(CHAT_ROUTES.NEW_CONVERSATION)}
              />
            </div>
          </div>
        </ChatContainer>
      </div>
    </div>
  );
};

ChatList.propTypes = {
  className: PropTypes.string.isRequired
};

export default ChatList;
